import React from "react";

export interface HeaderBusinessMenuItem {
  disabled?: boolean;
  icon?: React.ReactNode;
  id: string;
  label: string;
  onSelect?: () => void;
  to?: string;
  tone?: "default" | "danger";
}

export interface HeaderBusinessCompanyOption {
  id: string;
  label: string;
}

export interface HeaderBusinessProps {
  avatarSrc?: string;
  companyName: string;
  companyOptions?: readonly HeaderBusinessCompanyOption[];
  companyPlaceholder?: string;
  isMobileMenuOpen?: boolean;
  menuItems: readonly HeaderBusinessMenuItem[];
  notificationCount?: number;
  onCompanyChange?: (companyId: string | null) => void;
  onMobileMenuToggle?: () => void;
  onNotificationClick?: () => void;
  onSupportClick?: () => void;
  selectedCompanyId?: string | null;
  showMobileMenuToggle?: boolean;
  supportButtonLabel?: string;
}

export interface UseHeaderBusinessResult {
  closeMenu: () => void;
  initials: string;
  isMenuOpen: boolean;
  menuRef: React.MutableRefObject<HTMLDivElement | null>;
  selectedCompanyOption: HeaderBusinessCompanyOption | null;
  toggleMenu: () => void;
  triggerRef: React.MutableRefObject<HTMLButtonElement | null>;
}

function getInitials(companyName: string): string {
  const fragments = companyName
    .split(" ")
    .map((fragment) => fragment.trim())
    .filter(Boolean);

  if (fragments.length === 0) {
    return "OR";
  }

  return fragments
    .slice(0, 2)
    .map((fragment) => fragment.charAt(0).toUpperCase())
    .join("");
}

export function useHeaderBusiness({
  companyName,
  companyOptions = [],
  selectedCompanyId,
}: HeaderBusinessProps): UseHeaderBusinessResult {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const menuRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target;

      if (!(target instanceof Node)) {
        return;
      }

      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }

      setIsMenuOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return {
    closeMenu: () => setIsMenuOpen(false),
    initials: getInitials(companyName),
    isMenuOpen,
    menuRef,
    selectedCompanyOption: companyOptions.find((option) => option.id === selectedCompanyId) ?? null,
    toggleMenu: () => setIsMenuOpen((currentValue) => !currentValue),
    triggerRef,
  };
}
